import { toPublicAssetUrl, uploadSignedAsset } from "./uploadSignedAsset";
import {
  attachBridgeWindowListener,
  buildUnlayerImageBridgeInlineJS,
  clearActiveUploadContext,
  PICK_FILE_FOCUS_FALLBACK_MS,
  setActiveUploadContext,
  teardownUnlayerBridge,
} from "./unlayerBridge";
import { collectDesignImages, saveDesign } from "./unlayerDesign";

export {
  applyImageToTarget,
  setSelectedImageUrlViaBridge,
  UNLAYER_UPLOAD_BRIDGE_SOURCE,
} from "./unlayerBridge";
export { applyImageUrlById, collectDesignImages, patchImageById, saveDesign } from "./unlayerDesign";

const IMAGE_ACCEPT = "image/jpeg,image/png,image/gif,image/webp";
const REGISTERED_FLAG = "__cohoraImageUploadRegistered";

/** Lazy getter — origin is only known at runtime. */
export const getUnlayerImageBridgeInlineJS = () => buildUnlayerImageBridgeInlineJS();

const pickImageFile = () =>
  new Promise((resolve) => {
    let settled = false;
    let focusTimer = null;

    const input = document.createElement("input");
    input.type = "file";
    input.accept = IMAGE_ACCEPT;
    input.style.display = "none";

    const settle = (value) => {
      if (settled) {
        return;
      }
      settled = true;
      if (focusTimer) {
        window.clearTimeout(focusTimer);
        focusTimer = null;
      }
      window.removeEventListener("focus", onWindowFocus);
      input.removeEventListener("change", onChange);
      input.removeEventListener("cancel", onCancel);
      input.remove();
      resolve(value);
    };

    const onChange = () => {
      settle(input.files?.[0] ?? null);
    };

    const onCancel = () => {
      settle(null);
    };

    const onWindowFocus = () => {
      focusTimer = window.setTimeout(() => {
        if (document.body.contains(input)) {
          settle(null);
        }
      }, PICK_FILE_FOCUS_FALLBACK_MS);
    };

    input.addEventListener("change", onChange);
    input.addEventListener("cancel", onCancel);
    window.addEventListener("focus", onWindowFocus, { once: true });

    document.body.appendChild(input);
    input.click();
  });

export const probeImageSize = (file) =>
  new Promise((resolve) => {
    if (!file) {
      resolve(null);
      return;
    }

    const finish = (width, height) => {
      if (width > 0 && height > 0) {
        resolve({ width, height });
        return;
      }
      resolve(null);
    };

    if (typeof createImageBitmap === "function") {
      createImageBitmap(file)
        .then((bitmap) => {
          const size = { width: bitmap.width, height: bitmap.height };
          bitmap.close?.();
          finish(size.width, size.height);
        })
        .catch(() => {
          probeWithImageElement(file).then(resolve);
        });
      return;
    }

    probeWithImageElement(file).then(resolve);
  });

const probeWithImageElement = (file) =>
  new Promise((resolve) => {
    const objectUrl = URL.createObjectURL(file);
    const image = new Image();

    const cleanup = () => {
      URL.revokeObjectURL(objectUrl);
      image.onload = null;
      image.onerror = null;
    };

    image.onload = () => {
      const size =
        image.naturalWidth > 0 && image.naturalHeight > 0
          ? { width: image.naturalWidth, height: image.naturalHeight }
          : null;
      cleanup();
      resolve(size);
    };

    image.onerror = () => {
      cleanup();
      resolve(null);
    };

    image.src = objectUrl;
  });

export const uploadImageFile = async (file, description) => {
  const asset = await uploadSignedAsset({
    file,
    entity: "TEMPLATE",
    description,
  });

  const url = toPublicAssetUrl(asset?.url);

  if (!url) {
    throw new Error("Asset upload returned empty url");
  }

  return url;
};

/**
 * Upload file to CDN. Returns { url, images, size } so caller can pick target block.
 */
export const uploadImageToUnlayerEditor = async (editor, { description, onUploadError } = {}) => {
  if (!editor) {
    throw new Error("Editor is not ready");
  }

  try {
    const file = await pickImageFile();
    if (!file) {
      return null;
    }

    const [url, size] = await Promise.all([uploadImageFile(file, description), probeImageSize(file)]);
    const design = await saveDesign(editor);
    const images = collectDesignImages(design);

    return { url, images, design, size };
  } catch (error) {
    console.error("Unlayer CDN image upload failed", error);
    onUploadError?.(error?.message || "Image upload failed");
    throw error;
  }
};

export const registerUnlayerImageUpload = (
  editor,
  { description, onUploadError, onSelectionChange } = {}
) => {
  if (!editor) {
    return () => {};
  }

  setActiveUploadContext({
    editor,
    description,
    onUploadError,
    onSelectionChange,
  });

  attachBridgeWindowListener();

  if (!editor[REGISTERED_FLAG] && editor.registerCallback) {
    editor[REGISTERED_FLAG] = true;

    editor.registerCallback("selectImage", async (_data, done) => {
      try {
        const file = await pickImageFile();
        if (!file) {
          done({ url: "" });
          return;
        }
        const url = await uploadImageFile(file, description);
        done({ url });
      } catch (error) {
        console.error("Unlayer selectImage upload failed", error);
        onUploadError?.(error?.message || "Image upload failed");
        done({ url: "" });
      }
    });

    editor.registerCallback("image", (file, done) => {
      const attachment = file?.attachments?.[0];
      if (!attachment) {
        done({ progress: 100, abort: true });
        return;
      }
      uploadImageFile(attachment, description)
        .then((url) => done({ progress: 100, url }))
        .catch((error) => {
          console.error("Unlayer image upload failed", error);
          onUploadError?.(error?.message || "Image upload failed");
          done({ progress: 100, abort: true });
        });
    });
  }

  return () => {
    teardownUnlayerBridge(editor);
    clearActiveUploadContext();
  };
};

export default registerUnlayerImageUpload;
